import { io, Socket } from 'socket.io-client';
import {
  SubscribeTransactionRequest,
  SubscriptionConfirmedResponse,
  StatusUpdateResponse,
} from './types';

const BASE_URL = 'https://payamigo-integration.cryptosrvc-staging.com';

export class PayAmigoWebSocket {
  private socket: Socket | null = null;
  private baseUrl: string;
  private currentHandlers: {
    onConfirmed?: (data: SubscriptionConfirmedResponse) => void;
    onStatus?: (data: StatusUpdateResponse) => void;
    onError?: (data: any) => void;
  } = {};

  constructor(baseUrl: string = BASE_URL) {
    this.baseUrl = baseUrl;
  }

  public connect(): void {
    if (this.socket?.connected) {
      return;
    }

    this.socket = io(`${this.baseUrl}/v1/payments`, {
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      timeout: 10000,
      withCredentials: false,
    });

    this.socket.on('connect', () => {
      console.log('[payments] connected', this.socket?.id);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('[payments] disconnected:', reason);
    });

    this.socket.on('connect_error', (error) => {
      console.error('[payments] connect_error:', error?.message || error);
    });

    this.socket.on('error', (payload) => {
      console.error('[payments] gateway error:', payload);
    });

    // Handle additional Socket.IO events
    this.socket.on('reconnect', (attemptNumber) => {
      console.log('[payments] reconnected after', attemptNumber, 'attempts');
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log('[payments] reconnect attempt', attemptNumber);
    });

    this.socket.on('reconnect_error', (error) => {
      console.error('[payments] reconnect_error:', error?.message || error);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('[payments] reconnect_failed: all reconnection attempts exhausted');
    });

    // Log all incoming messages for debugging
    this.socket.onAny((eventName, ...args) => {
      console.log('[payments] event received:', eventName, args);
    });
  }

  public disconnect(): void {
    if (this.socket) {
      this.socket.offAny(); // Remove the catch-all listener
      this.socket.disconnect();
      this.socket = null;
    }
  }

  public subscribeToTransaction(
    transactionId: string,
    onSubscribed?: (data: SubscriptionConfirmedResponse) => void,
    onStatusUpdate?: (data: StatusUpdateResponse) => void,
    onError?: (data: any) => void
  ): void {
    if (!this.socket || typeof this.socket.emit !== 'function') {
      throw new Error('Invalid socket instance');
    }
    if (!transactionId) {
      throw new Error('transactionId is required');
    }

    // Unsubscribe from previous transaction first
    this.unsubscribeFromTransaction();

    console.log('[payments] Subscribing to transaction:', transactionId);

    // Store handlers for proper cleanup
    const handleConfirmed = (p: SubscriptionConfirmedResponse) => {
      console.log('[payments] 🎯 subscription_confirmed event received:', p);
      if (onSubscribed) onSubscribed(p);
    };
    const handleStatus = (p: StatusUpdateResponse) => {
      console.log('[payments] 🎯 status_update event received:', p);
      if (onStatusUpdate) onStatusUpdate(p);
    };
    const handleError = (p: any) => {
      console.log('[payments] 🎯 error event received:', p);
      if (onError) onError(p);
    };

    this.currentHandlers = {
      onConfirmed: handleConfirmed,
      onStatus: handleStatus,
      onError: handleError,
    };

    // Register event listeners
    this.socket.on('subscription_confirmed', handleConfirmed);
    this.socket.on('status_update', handleStatus);
    this.socket.on('error', handleError);

    // Emit subscription request
    const request: SubscribeTransactionRequest = { transactionId };
    this.socket.emit('subscribe_transaction', request);
  }

  public unsubscribeFromTransaction(): void {
    if (this.socket && this.currentHandlers) {
      console.log('[payments] Unsubscribing from transaction');
      
      if (this.currentHandlers.onConfirmed) {
        this.socket.off('subscription_confirmed', this.currentHandlers.onConfirmed);
      }
      if (this.currentHandlers.onStatus) {
        this.socket.off('status_update', this.currentHandlers.onStatus);
      }
      if (this.currentHandlers.onError) {
        this.socket.off('error', this.currentHandlers.onError);
      }

      this.currentHandlers = {};
    }
  }

  public isConnected(): boolean {
    return this.socket?.connected || false;
  }
}

export const payAmigoWebSocket = new PayAmigoWebSocket();

